import { useCallback, useMemo, useState } from 'react';
import { prefersReducedMotion } from './useMarketingScroll';

export interface HeroParallax {
  /** Cursor offset from the container centre, normalised to −1…1. */
  x: number;
  y: number;
  onMouseMove: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  /** Per-layer depth translation, e.g. `layer(-22)`. */
  layer: (depth: number) => string;
  /** Shared easing for every parallax layer. */
  transition: string;
}

const isTouch = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

/**
 * Mouse parallax for the hero art.
 *
 * Disabled for touch pointers (there is no hover to track) and for viewers who
 * asked for reduced motion; both cases pin the offsets at 0 so every layer
 * renders in its resting position.
 */
export const useHeroParallax = (): HeroParallax => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const enabled = useMemo(() => !isTouch() && !prefersReducedMotion(), []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!enabled) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setOffset({
        x: ((event.clientX - rect.left) / rect.width - 0.5) * 2,
        y: ((event.clientY - rect.top) / rect.height - 0.5) * 2,
      });
    },
    [enabled]
  );

  const onMouseLeave = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  const layer = useCallback(
    (depth: number) => `translate(${offset.x * depth}px, ${offset.y * depth}px)`,
    [offset.x, offset.y]
  );

  return {
    x: offset.x,
    y: offset.y,
    onMouseMove,
    onMouseLeave,
    layer,
    transition: 'transform 0.2s ease-out',
  };
};
